import { Button, Flex, Typography } from 'antd';
import { useGetChannels } from './hooks/use-get-channels';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { LoadingState } from '@shared/ui/loading-state/loading-state';
import { Trans, useTranslation } from 'react-i18next';
import { ChannelsTable } from './ui/channels-table/channels-table';
import { PageHeader } from '@shared/ui/page-header';
import styles from './styles.module.scss';
import { useToggle } from '@shared/hooks/use-toggle';
import { ChannelCreate } from './ui/create-modal/create-modal';
import { generatePath, useNavigate } from 'react-router-dom';
import { Routes } from '@shared/constants/routes';
import { ChannelsEmptyState } from './ui/empty-state/empty-state';
import { toChannelCreateRouteType, type ChannelCreatableType } from './lib/channel-type';
import { useDeleteChannel } from './hooks/use-delete-channel';
import { useGlobalConfirmModalActions } from '@shared/redux/ducks/global-confirm-modal';
import { useActivateChannel } from './hooks/use-activate-channel';
import { copyToClipboard } from '@shared/utils/copy-to-clipboard';

export const Channels = () => {
  const { Text: AntdText } = Typography;
  const { t } = useTranslation('translation', { keyPrefix: 'channels' });

  const navigate = useNavigate();

  const {
    state: isCreateChannelsModalOpen,
    setFalse: closeCreateChannelsModal,
    setTrue: openCreateChannelsModal,
  } = useToggle();

  const { openConfirmModal } = useGlobalConfirmModalActions();
  const { deleteChannel } = useDeleteChannel();
  const { updateChannel, isLoading: isToggleLoading, loadingChannelId } = useActivateChannel();

  const handleSelectChannelToUpdate = (channelId: number) => {
    navigate(
      generatePath(Routes.channels.update, {
        channelId,
      }),
    );
  };

  const handleSelectChannelType = (channelType: ChannelCreatableType) => {
    navigate(
      generatePath(Routes.channels.create, {
        type: toChannelCreateRouteType(channelType),
      }),
    );
  };

  const handleCopyWidgetScript = (channelId: number) => {
    const script = `<script src="${window.location.origin}/api/widget/script.js" defer="" data-channel_id="${channelId}"></script>`;
    void copyToClipboard(script);
  };

  const handleOpenConfirmModal = (id: number) => {
    openConfirmModal({
      title: t('deleteModal.title'),
      text: (
        <Trans
          t={t}
          i18nKey="deleteModal.text"
          values={{
            channel: channels?.find((channel) => channel.id === id)?.name,
            interpolation: { escapeValue: false },
          }}
          components={{ bold: <AntdText strong /> }}
        />
      ),
      onConfirm: () => deleteChannel(id),
    });
  };

  const {
    channels,
    isLoading: isChannelsLoading,
    isError: isChannelsError,
    isEmpty: isChannelsEmpty,
    isFetching: isChannelsFetching,
    refetch: refetchChannels,
  } = useGetChannels();

  return (
    <Flex vertical gap={20} className={styles.root}>
      <PageHeader title={t('channels')} />

      <Flex vertical className={styles.container} gap={16}>
        {(isChannelsLoading || isChannelsFetching) && <LoadingState />}
        {!isChannelsLoading && isChannelsError && <FetchingErrorState refetch={refetchChannels} />}

        {!isChannelsLoading && !isChannelsError && !isChannelsEmpty && (
          <div className={styles.tableWrapper}>
            <ChannelsTable
              channels={channels}
              isToggleLoading={isToggleLoading}
              loadingChannelId={loadingChannelId}
              onToggleActive={updateChannel}
              onSelectChannelToUpdate={handleSelectChannelToUpdate}
              onDeleteChannel={handleOpenConfirmModal}
              onCopyWidgetScript={handleCopyWidgetScript}
            />
          </div>
        )}

        {!isChannelsLoading && !isChannelsError && !isChannelsEmpty && (
          <Flex>
            <Button
              onClick={openCreateChannelsModal}
              type="primary"
              className={styles.button}
              size="large"
            >
              {t('addChannel')}
            </Button>
          </Flex>
        )}

        {!isChannelsLoading && !isChannelsError && isChannelsEmpty && (
          <ChannelsEmptyState onSelectType={handleSelectChannelType} />
        )}
      </Flex>

      <ChannelCreate
        isOpen={isCreateChannelsModalOpen}
        onClose={closeCreateChannelsModal}
        onSelectType={handleSelectChannelType}
      />
    </Flex>
  );
};
